import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { MentionSearchService } from './mention-search.service';
import { SearchCriteriaQueryModel } from '../../../api/mentions/model/search-criteria-query.model';
import { MentionSearchQueryModel } from '../../Shared/models/mention-search-query.model';
import { DuplicatesStatusEnum } from '../../../Common/Filter/enums/duplicates-status.enum';
import { MentionsService } from './mentions.service';
import { MonitoringViewModesEnum } from '@Monitor/Shared/enums/monitoring-view-modes.enum';
import { SavedFilter } from '../../../Common/Filter/models/saved-filter.model';
import { UserAppMonitoringDisplaySettingsModel } from '@Core/Users/models/user-app-default-display-preferences.model';

@Injectable()
export class SearchResultsService implements OnDestroy {
    /** A list of subscription objects that will be unsubscribed when this service is destroyed */
    private subscriptions: Subscription[] = [];

    /** Flag that determines if filters should be locked when switching between different routes */
    private locked: boolean = false;

    /** Session key for storing a search query object containing all of the filters */
    private sessionKey: string = 'agpr-quick-filters';

    /**
     * Toggle for mentions being normal or compact size
     * NOTE: Stored here so it will stay with you when you leave a results page
     */
    public mentionsCompact: boolean = false;

    /**
     * Session key for storing the state of the duplicates "filter"
     * Duplicates is separate, because the search criteria only keeps the on/off state, and not the group state
     */
    private sessionKeyDuplicates: string = 'agpr-duplicates';

    /** Session key for storing saved filter IDs */
    private sessionKeySavedFilterIds: string = 'agpr-saved-filter-ids';

    private currentViewMode: MonitoringViewModesEnum = MonitoringViewModesEnum.MENTION;
    private isViewModeChange: boolean;
    private currentDisplaySettings$ = new BehaviorSubject<UserAppMonitoringDisplaySettingsModel>(null);

    constructor(
        private mentionSearchService: MentionSearchService,
        private mentionsService: MentionsService
    ) {
        this.loadLockedFiltersFromSession();

        this.subscriptions.push(
            this.mentionSearchService.getFilters().subscribe(
                (filters) => {
                    this.updateFiltersSessionStorage();
                }
            ),
            this.mentionSearchService.getMentions().subscribe(
                mentions => {
                    this.mentionsService.setMentions(mentions);
                    if (mentions && mentions.length) {
                        this.mentionsService.setFocusedMention(mentions[0]);
                    }
                }
            ),
            this.mentionSearchService.getMentionCount().subscribe(
                count => {
                    this.mentionsService.setMentionsCount(count);
                }
            )
        );
    }

    /**
     * Handles unsubscribing when service is destroyed
     */
    ngOnDestroy() {
        this.subscriptions.forEach(
            subscription => subscription.unsubscribe()
        );
    }

    //#region Locked filters handling
    /**
     * Gets the locked state of filters
     */
    public filtersLocked(): boolean {
        return this.locked;
    }

    /**
     * Sets the locked state of the filters
     * @param lock Flag for the locked state
     */
    public setFiltersLocked(lock: boolean): void {
        this.locked = lock;
        this.updateFiltersSessionStorage();
    }

    /**
     * Updates the session storage for locked filters
     */
    private updateFiltersSessionStorage(): void {
        if (this.locked) {
            const criteria = this.mentionSearchService.generateFilterSearchCriteria();
            const apiQuery = criteria.query.getApiEntity();

            sessionStorage.setItem(this.sessionKey, JSON.stringify(apiQuery));
            sessionStorage.setItem(this.sessionKeyDuplicates, this.mentionSearchService.duplicates.getValue());
            sessionStorage.setItem(this.sessionKeySavedFilterIds, JSON.stringify(this.mentionSearchService.savedFilters.getValue()?.map(x => x.toApiModel())));
        } else {
            sessionStorage.removeItem(this.sessionKey);
            sessionStorage.removeItem(this.sessionKeyDuplicates);
        }
    }
    /**
     * Gets the locked filters from the session
     */
    loadLockedFiltersFromSession(): void {
        const item: string = sessionStorage.getItem(this.sessionKey);

        if (item) {
            try {
                const apiQuery: SearchCriteriaQueryModel = JSON.parse(item);
                const query: MentionSearchQueryModel = new MentionSearchQueryModel(apiQuery);

                this.mentionSearchService.populateFiltersFromSearchQuery(query);
                this.mentionSearchService.setDateQuery(query.dateQuery);
                this.mentionSearchService.duplicates.setValue(
                    sessionStorage.getItem(this.sessionKeyDuplicates) as DuplicatesStatusEnum
                );
                this.mentionSearchService.savedFilters.setValue(
                    JSON.parse(sessionStorage.getItem(this.sessionKeySavedFilterIds)).map(x => new SavedFilter(x))
                );
                this.locked = true;
                this.mentionSearchService.applyFilters(false);
            } catch (err) {
                this.mentionSearchService.clearFilters();
            }
        }
    }
    //#endregion

    setCurrentViewMode(mode: MonitoringViewModesEnum): void {
        this.currentViewMode = mode;
    }

    getCurrentViewMode(): MonitoringViewModesEnum {
        return this.currentViewMode;
    }

    setIsViewModeChange(isChange: boolean): void {
        this.isViewModeChange = isChange;
    }

    getIsViewModeChange(): boolean {
        return this.isViewModeChange;
    }

    /**
     * Updates the current display settings in this service.
     * Emits the new settings to all subscribers.
     */
    setCurrentDisplaySettings(settings: UserAppMonitoringDisplaySettingsModel): void {
        this.currentDisplaySettings$.next(settings);
    }

    /**
     * Returns an observable of the current display settings.
     * Components can subscribe to this to get updates whenever the settings change.
     */
    getCurrentDisplaySettings(): Observable<UserAppMonitoringDisplaySettingsModel> {
        return this.currentDisplaySettings$.asObservable();
    }
}
